/**
 * Time until each funnel step is usable, sent to Sentry as a metric.
 *
 * Page load timings (LCP and friends) stop at the first big paint, which on
 * these pages is often a shell still waiting for data. This records when the
 * person can actually do the step: the candidate form can be submitted, the
 * assessments list shows, and so on. Metrics are not sampled, so every visit
 * counts. Query them in Sentry as `funnel.step_ready`, split by `step`, `load`,
 * `device` and `time_zone`.
 */
import { whenSentryBrowserReady } from "@/utils/sentryClient";

export type FunnelStep =
  | "signup_branding"
  | "candidate_form"
  | "assessments_list"
  | "create_form";

// performance.now() when the current in-app navigation started; null until
// the first in-app navigation, so the first page is timed from page load.
let navigationStartedAt: number | null = null;
const reported = new Set<string>();

/** Called by the router for every navigation after the first page. */
export function markInAppNavigationStart(): void {
  navigationStartedAt = performance.now();
  reported.clear();
}

function deviceClass(): string {
  try {
    return window.matchMedia("(pointer: coarse)").matches ? "touch" : "desktop";
  } catch {
    return "unknown";
  }
}

function timeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "unknown";
  } catch {
    return "unknown";
  }
}

/** Report once per page visit that `step` is usable. */
export function reportFunnelStepReady(step: FunnelStep): void {
  if (reported.has(step) || typeof performance === "undefined") return;
  reported.add(step);

  const firstLoad = navigationStartedAt === null;
  const ms = Math.round(
    firstLoad ? performance.now() : performance.now() - (navigationStartedAt as number)
  );
  try {
    // Same moment, visible in DevTools and to lab benchmarks.
    (performance.mark as (name: string, options?: unknown) => void)(
      `funnel.step_ready:${step}`,
      { detail: { ms } }
    );
  } catch {
    // Older browsers without mark options.
  }
  const attributes = {
    step,
    load: firstLoad ? "first" : "in_app",
    device: deviceClass(),
    time_zone: timeZone(),
  };

  whenSentryBrowserReady((Sentry) => {
    Sentry.metrics.distribution("funnel.step_ready", ms, {
      unit: "millisecond",
      attributes,
    });
  });
}
